/**
 * Putting a whole piece in a link.
 *
 * Everything here is reproducible from its settings, which was the point of
 * seeding every random choice, and yet a track someone liked used to evaporate
 * when they closed the tab. A link fixes that and makes the tool something
 * people can send each other, which no amount of polish does on its own.
 *
 * Two encodings, and the short one is the reason this is worth doing properly:
 *
 *   #r=driving.482917   a plain roll, which is most of them
 *   #p=<base64>         anything that has been tweaked
 *
 * A rolled track is completely described by its style and its seed, so it gets
 * a link short enough to read out. Only a piece someone has actually changed
 * needs the long form.
 */

import { randomPiece } from "./randomise.js";
import type { Piece } from "./piece.js";

/** Base64 that survives being in a URL, and without the padding noise. */
function toBase64Url(text: string): string {
  const bytes = new TextEncoder().encode(text);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function fromBase64Url(text: string): string | null {
  try {
    const padded = text.replace(/-/g, "+").replace(/_/g, "/");
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  } catch {
    return null;
  }
}

/** Names that could be confused with the separator or the encoding. */
const SIMPLE_NAME = /^[a-z]+$/;

/**
 * JSON with the keys in a fixed order, so two pieces that say the same thing
 * compare equal regardless of the order they were built up in.
 *
 * Needed because a piece read back out of a form has its fields in the form's
 * order, not the generator's, and comparing raw `JSON.stringify` output would
 * call every one of those different.
 */
export function canonical(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value) ?? "null";
  if (Array.isArray(value)) return `[${value.map(canonical).join(",")}]`;
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonical(v)}`).join(",")}}`;
}

/**
 * Is this piece exactly what rolling that style with that seed produces?
 *
 * Checked by regenerating rather than trusting a flag, because the piece can be
 * changed from half a dozen places and a flag would eventually lie. Getting
 * this wrong is not cosmetic: a short link that is not really that roll opens a
 * different track for whoever clicks it.
 *
 * Compared on the fields that reach the renderer. A piece that has been through
 * the editor's form carries a couple of settings the form does not hold, and
 * none of them change a sample.
 */
function isPlainRoll(piece: Piece, mood: string): boolean {
  if (piece.seed === undefined) return false;
  try {
    return canonical(audible(randomPiece(piece.seed, { mood }))) === canonical(audible(piece));
  } catch {
    return false;
  }
}

/**
 * Which settings each layer actually reads.
 *
 * Listed rather than inferred, because the editor's form carries a few that the
 * renderer ignores for that role: a density on a pad, an instrument on
 * percussion. Comparing those would call two identical-sounding pieces
 * different and cost every shared roll its short link.
 */
const READS: Record<string, readonly string[]> = {
  pad: ["instrument", "octave", "volume", "enterAt"],
  bass: ["instrument", "octave", "volume", "enterAt"],
  rhythm: ["instrument", "octave", "volume", "enterAt", "density", "figure", "motifs", "variation"],
  melody: ["instrument", "octave", "volume", "enterAt", "density", "figure", "motifs", "variation"],
  perc: ["volume", "enterAt", "groove", "pattern"],
};

/** The parts of a piece that change what you hear. */
function audible(piece: Piece): unknown {
  const layers: Record<string, unknown> = {};
  for (const [role, spec] of Object.entries(piece.layers)) {
    if (!spec) continue;
    const kept: Record<string, unknown> = {};
    for (const field of READS[role] ?? []) {
      const value = (spec as unknown as Record<string, unknown>)[field];
      if (value !== undefined) kept[field] = value;
    }
    layers[role] = kept;
  }
  return {
    key: piece.key, tempo: piece.tempo, progression: piece.progression,
    duration: piece.duration, barsPerChord: piece.barsPerChord, seed: piece.seed,
    loop: piece.loop, space: piece.space,
    fadeIn: piece.fadeIn, fadeOut: piece.fadeOut, layers,
  };
}

/**
 * @param mood the style it was rolled from, if it came from one at all
 * @returns the fragment to put after the `#`
 */
export function encodePiece(piece: Piece, mood?: string): string {
  if (mood && SIMPLE_NAME.test(mood) && isPlainRoll(piece, mood)) {
    return `r=${mood}.${piece.seed}`;
  }
  return `p=${toBase64Url(JSON.stringify(piece))}`;
}

export interface Shared {
  piece: Piece;
  /** Set when the link was a plain roll, so the chip can be re-selected. */
  mood?: string;
}

/**
 * Read a piece back out of a fragment.
 *
 * Returns null rather than throwing on anything malformed. A bad link is
 * someone else's typo, and the right response is to open normally rather than
 * to show a stranger an error about base64.
 */
export function decodePiece(fragment: string): Shared | null {
  const text = fragment.replace(/^#/, "");
  if (!text) return null;

  const roll = /^r=([a-z]+)\.(\d+)$/.exec(text);
  if (roll) {
    const [, mood, seed] = roll;
    try {
      const piece = randomPiece(Number(seed), { mood });
      // An unknown style falls back to a random one inside randomPiece, which
      // would silently hand over a different track. Better to open normally.
      return piece.name.startsWith(mood) ? { piece, mood } : null;
    } catch {
      return null;
    }
  }

  if (!text.startsWith("p=")) return null;
  const json = fromBase64Url(text.slice(2));
  if (!json) return null;

  try {
    const piece = JSON.parse(json) as Piece;
    // Enough of a check that a truncated link does not reach the renderer and
    // fail somewhere much less explicable.
    if (typeof piece?.key !== "string" || typeof piece?.tempo !== "number") return null;
    if (typeof piece?.progression !== "string" || typeof piece?.layers !== "object") return null;
    return { piece };
  } catch {
    return null;
  }
}
